import classNames from './ProfileCard.module.scss';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import commonUtils from '@/utils/common';
import UseLevel from '@/hooks/UseLevel';
import { Streak } from '@/types/user';

import SectionSeperator from '@/components/SectionSeperator/SectionSeperator';
import UserStreak from '@/components/UserStreak/UserStreak';

import diamond from '@/assets/svg/diamond-large.svg';
import classroomImg from '@/assets/svg/classroom.svg';
import UseScreenSize from '@/hooks/UseScreenSize';
import { useAppSelector } from '@/store';
import { interfaceLanguageSelector } from '@/store/reducers/auth';

interface ProfileCardProps {
  className?: string;
  classroom?: string;
  level?: number;
  grade?: string;
  school?: string | null;
  country?: string | null;
  streak?: Streak[] | null;
}

const ProfileCard = ({
  className,
  classroom,
  level,
  grade,
  school,
  country,
  streak,
}: ProfileCardProps) => {
  const { t } = useTranslation();
  const { isDesktop } = UseScreenSize();
  const { getLevelText } = UseLevel();
  const levelText = getLevelText(level);
  const interfaceLanguage = useAppSelector(interfaceLanguageSelector);

  const isHebrew = interfaceLanguage?.code === 'he';

  const gradeText = !isHebrew ? ` ${t('grade')}` : '';

  const userGrade = grade ?? '';
  let gradeValue = '';

  const isUniversityGrade = grade?.toLowerCase() === 'university';
  const isNoneGrade = grade?.toLowerCase() === 'none';

  if (!isNoneGrade) {
    if (isUniversityGrade) {
      gradeValue = t('university');
    } else {
      gradeValue =
        isNaN(+userGrade) || Number.isInteger(userGrade)
          ? t(`grade_${commonUtils.fixGradeSymbol(userGrade)}`)
          : userGrade;
    }
  }

  return (
    <div className={classes(classNames.profileCard, className)}>
      <div className={classNames.top}>
        {isDesktop && <SVG src={classroomImg} />}
        <h2>{classroom}</h2>
      </div>
      <div className={classNames.info}>
        <div className={classNames.line}>
          <span className={classNames.label}>{t('level')}</span>
          <span className={classNames.value}>{t(levelText ?? '')}</span>
          <SVG
            className={classes(classNames.diamond, {
              [classNames.diamondRtl]: interfaceLanguage?.direction === 'rtl',
            })}
            src={diamond}
          />
        </div>
        {gradeValue && (
          <div className={classNames.line}>
            <span className={classNames.label}>{t('grade')}</span>
            <span
              className={classNames.value}
            >{`${gradeValue}${gradeText}`}</span>
          </div>
        )}
        <div className={classNames.line}>
          <span className={classNames.label}>{t('schoolName')}</span>
          <span className={classNames.value}>{school ?? 'N/A'}</span>
        </div>
        <div className={classNames.line}>
          <span className={classNames.label}>{t('country')}</span>
          <span className={classNames.value}>{country ?? 'N/A'}</span>
        </div>
      </div>
      {isDesktop && <SectionSeperator className="my-6" />}
      {streak && isDesktop && (
        <div className={classNames.streakContainer}>
          <span className={classNames.title}>{t('yourStreak')}</span>
          <UserStreak showTitle={false} streak={streak} />
        </div>
      )}
    </div>
  );
};

export default ProfileCard;
